/** Routes that use the dark navy shell (lists hub + list editor). */
export function usesDarkAppChrome(pathname) {
  return pathname === "/grocery/lists" || pathname === "/grocery";
}

/** Nav chrome on pages with the dark auth background (theme-independent). */
export const authNavBarSx = {
  boxShadow: "none",
  backgroundImage: "none",
  borderBottom: "none",
  color: "#fff !important",
  "& .MuiToolbar-root": { color: "#fff" },
  "& .MuiButton-root": { color: "#fff !important" },
  "& .MuiButton-root .MuiSvgIcon-root": { color: "#fff !important" },
  "& .MuiIconButton-root": { color: "#fff !important" },
  "& .MuiIconButton-root .MuiSvgIcon-root": { color: "#fff !important" },
};

/** Large hero logo on auth / landing pages. */
export const authPageLogoSx = {
  opacity: 1,
  filter: "drop-shadow(0 8px 24px rgba(0, 0, 0, 0.35))",
};

/** Navbar content column — lines up with dashboard / profile pages. */
export const authNavContentSx = {
  width: "100%",
  maxWidth: 880,
  mx: "auto",
  px: { xs: 2, sm: 3 },
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
};

export const AUTH_NAV_ICON_SIZE = 44;

/** Small navbar logo on auth backdrop pages. */
export const authNavLogoSx = {
  opacity: 1,
  filter: "drop-shadow(0 2px 10px rgba(0, 0, 0, 0.35))",
};

/** Hit target for the brand mark — logo only, no circle frame. */
export const authNavBrandButtonSx = {
  p: 0.25,
  width: AUTH_NAV_ICON_SIZE + 8,
  height: AUTH_NAV_ICON_SIZE + 8,
  minWidth: AUTH_NAV_ICON_SIZE + 8,
  minHeight: AUTH_NAV_ICON_SIZE + 8,
  flexShrink: 0,
  borderRadius: 1,
  border: "none",
  bgcolor: "transparent",
  overflow: "visible",
  transition: "opacity 0.15s, transform 0.15s",
  "&:hover": {
    bgcolor: "transparent",
    opacity: 0.92,
    transform: "scale(1.04)",
  },
};

/** Profile avatar in the top nav. */
export const authNavAvatarSx = {
  width: AUTH_NAV_ICON_SIZE,
  height: AUTH_NAV_ICON_SIZE,
  border: "2px solid rgba(255, 255, 255, 0.32)",
  boxShadow: "0 2px 14px rgba(0, 0, 0, 0.4)",
  bgcolor: "#070d18",
  fontSize: "0.95rem",
  fontWeight: 700,
};

/** Brand button on standard (theme) pages. */
export const appNavBrandButtonSx = {
  p: 0.5,
  width: AUTH_NAV_ICON_SIZE + 8,
  height: AUTH_NAV_ICON_SIZE + 8,
  minWidth: AUTH_NAV_ICON_SIZE + 8,
  minHeight: AUTH_NAV_ICON_SIZE + 8,
  flexShrink: 0,
  borderRadius: "50%",
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  overflow: "hidden",
  transition: "border-color 0.15s, background-color 0.15s, box-shadow 0.15s",
  "&:hover": {
    bgcolor: "action.hover",
    borderColor: "primary.main",
    boxShadow: 1,
  },
};

/** Profile avatar on standard (theme) pages. */
export const appNavAvatarSx = {
  width: AUTH_NAV_ICON_SIZE,
  height: AUTH_NAV_ICON_SIZE,
  border: "2px solid",
  borderColor: "divider",
  fontSize: "0.95rem",
  fontWeight: 700,
};

/** Profile menu button on standard (theme) pages. */
export const appNavProfileButtonSx = {
  p: 0.25,
  width: AUTH_NAV_ICON_SIZE + 8,
  height: AUTH_NAV_ICON_SIZE + 8,
  minWidth: AUTH_NAV_ICON_SIZE + 8,
  minHeight: AUTH_NAV_ICON_SIZE + 8,
  flexShrink: 0,
  borderRadius: "50%",
  transition: "background-color 0.15s, box-shadow 0.15s",
  "&:hover": {
    bgcolor: "action.hover",
    boxShadow: 1,
  },
};

/** Nav chrome on standard theme pages (no photo / navy backdrop). */
export const appNavBarSx = {
  boxShadow: "none",
  backgroundImage: "none",
  bgcolor: "background.paper",
  color: "text.primary",
  borderBottom: "1px solid",
  borderColor: "divider",
};

/** Profile menu button in the top nav. */
export const authNavProfileButtonSx = {
  p: 0.25,
  width: AUTH_NAV_ICON_SIZE + 8,
  height: AUTH_NAV_ICON_SIZE + 8,
  minWidth: AUTH_NAV_ICON_SIZE + 8,
  minHeight: AUTH_NAV_ICON_SIZE + 8,
  flexShrink: 0,
  borderRadius: "50%",
  transition: "background-color 0.15s, box-shadow 0.15s",
  "&:hover": {
    bgcolor: "rgba(255, 255, 255, 0.08)",
    boxShadow: "0 0 14px rgba(255, 255, 255, 0.12)",
  },
};

/** TV / kiosk dark navy shell — matches /tv pages (always dark). */
export const TV_SHELL_BG = "#070d18";

/** App content panels — follow light/dark theme (not locked to TV navy). */
export const tvNavyPanelSx = {
  bgcolor: "background.paper",
  color: "text.primary",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
};

/** Navy content panel with default padding (workflow pages, settings, standalone apps). */
export const navyContentPanelSx = {
  ...tvNavyPanelSx,
  p: { xs: 1.5, sm: 2 },
};

/** Subtle inset panel on an existing surface (e.g. Apps grid inside Dashboard). */
export const tvNavyInsetPanelSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  bgcolor: "action.hover",
  color: "text.primary",
};

/** Muted body copy — theme-aware (`text.secondary` palette path). */
export const tvNavyTextMuted = "text.secondary";

/** Full-viewport dark navy shell (CS2 TV, lists hub, etc.). */
export const tvShellSx = {
  minHeight: "100vh",
  bgcolor: TV_SHELL_BG,
  color: "#fff",
  px: { xs: 2, md: 5 },
  py: { xs: 2, md: 3 },
  boxSizing: "border-box",
};

/** Centered content column on TV / navy pages. */
export const tvContentMaxSx = {
  maxWidth: 900,
  mx: "auto",
  width: "100%",
};

/** CS2-style vertical list row on dark navy surfaces. */
export const tvListRowSx = {
  borderRadius: 2,
  mb: 1,
  border: "1px solid rgba(255, 255, 255, 0.15)",
  bgcolor: "rgba(255, 255, 255, 0.04)",
  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
};

/** Frosted panel for content on top of the photo background. */
export const glassPanelSx = {
  bgcolor: "rgba(255, 255, 255, 0.08)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(255, 255, 255, 0.14)",
  borderRadius: 3,
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.28)",
};

/** Frosted glass surface for MUI Dialog paper. */
export const frostedDialogPaperSx = {
  bgcolor: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255, 255, 255, 0.16)",
  borderRadius: 3,
  boxShadow: "0 16px 48px rgba(0, 0, 0, 0.38)",
  backgroundImage: "none",
};

export const frostedDialogBackdropSx = {
  backgroundColor: "rgba(0, 0, 0, 0.45)",
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
};

export const frostedDialogPaperProps = {
  elevation: 0,
  sx: frostedDialogPaperSx,
};

/** Frosted dialog on dark / TV surfaces (light text). */
export const frostedDialogPaperPropsTv = {
  elevation: 0,
  sx: { ...frostedDialogPaperSx, color: "#fff" },
};

/** Merge extra layout styles onto the frosted dialog paper. */
export function frostedDialogPaperPropsWith(extraSx = {}) {
  return {
    elevation: 0,
    sx: { ...frostedDialogPaperSx, ...extraSx },
  };
}

/** Dropdown menu on dark navy pages (lists Options, etc.). */
export const tvNavyMenuPaperProps = {
  elevation: 0,
  sx: {
    minWidth: 200,
    bgcolor: TV_SHELL_BG,
    color: "#fff",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: 2,
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.45)",
    backgroundImage: "none",
    "& .MuiMenuItem-root": {
      color: "#fff",
      fontSize: "0.9rem",
      "&:hover": { bgcolor: "rgba(255, 255, 255, 0.08)" },
      "&.Mui-selected": { bgcolor: "rgba(77, 159, 255, 0.2)" },
      "&.Mui-focusVisible": { bgcolor: "rgba(255, 255, 255, 0.08)" },
    },
    "& .MuiDivider-root": {
      borderColor: "rgba(255, 255, 255, 0.12)",
    },
  },
};

export const tvNavyMenuItemDestructiveSx = {
  color: "rgba(255, 140, 140, 0.95) !important",
  "&:hover": { bgcolor: "rgba(255, 120, 120, 0.12) !important" },
};

/** MUI Dialog paper on dark navy surfaces (dashboard, lists, etc.). */
export const tvNavyDialogPaperProps = {
  elevation: 0,
  sx: {
    bgcolor: TV_SHELL_BG,
    color: "#fff",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: 3,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
    backgroundImage: "none",
  },
};

/** Outlined inputs inside themed dialogs / settings panels. */
export const tvNavyDialogFieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "text.primary",
    bgcolor: "action.hover",
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "text.secondary" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
  "& .MuiInputLabel-root": { color: "text.secondary" },
  "& .MuiInputLabel-root.Mui-focused": { color: "primary.main" },
  "& .MuiSelect-icon": { color: "text.secondary" },
  "& .MuiFormHelperText-root": { color: "text.secondary" },
};
